import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { OrderPhoto } from '../pages/orders-new/models/order.dto';
import { ItemPhotoComponent } from '../item-photo/item-photo.component';

/**
 * An order's face (0085): the photo of one of its photographed positions, flipped to the
 * next by a click, round and round, so a multi-position order is recognisable by every
 * product in it. Manual on purpose — nothing in a list moves on its own, and the first face
 * stays the stable one the eye learns. Dumb like item-photo underneath: the faces arrive in
 * the parent's DTO, and which one shows is view state only, never stored or persisted.
 */
@Component({
  selector: 'order-face',
  templateUrl: './order-face.component.html',
  styleUrl: './order-face.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ItemPhotoComponent],
  host: {
    '[class.order-face-multi]': 'multi()',
    '(click)': 'next($event)'
  }
})
export class OrderFaceComponent {
  readonly photos = input.required<OrderPhoto[]>();
  readonly size = input<'row' | 'card'>('row');

  readonly index = signal(0);

  readonly multi = computed(() => this.photos().length > 1);
  readonly photo = computed(() => this.photos()[this.index()]);

  // the position's name, and where it stands among the faces when there is more than one
  readonly label = computed(() => {
    const photo = this.photo();

    if (!photo) {
      return '';
    }

    return this.multi() ? `${photo.itemName} (${this.index() + 1}/${this.photos().length})` : photo.itemName;
  });

  constructor() {
    // a reloaded list (or a re-run search) brings new arrays — every face starts from the first again
    effect(() => {
      this.photos();
      this.index.set(0);
    });
  }

  // the face sits inside a row or card that is a routerLink — flipping must not navigate;
  // a single face lets the click fall through to the row as before
  next(event: MouseEvent): void {
    if (!this.multi()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.index.update(index => (index + 1) % this.photos().length);
  }
}
